import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import { refreshQueries } from '@shared/api/hooks/refresh-queries';
import { getAppIngredientPickerListQueryKey } from '@shared/api/lib/app/app';
import {
  getPurchasedGoodsFoodListQueryKey,
  getPurchasedGoodsFoodRecordsListQueryKey,
  usePurchasedGoodsDestroy,
  usePurchasedGoodsFoodCoverageRetrieve,
  usePurchasedGoodsFoodList,
  usePurchasedGoodsFoodListInfinite,
  usePurchasedGoodsFoodPartialUpdate,
  usePurchasedGoodsFoodPreviewRetrieve,
  usePurchasedGoodsFoodRecordsList,
  usePurchasedGoodsFoodRecordsPartialUpdate,
  usePurchasedGoodsFoodRecordsUpdate,
  usePurchasedGoodsFoodRetrieve,
  usePurchasedGoodsFoodSpendRetrieve,
  usePurchasedGoodsRecordsDestroy,
} from '@shared/api/lib/purchased-goods/purchased-goods';
import { getSoldGoodsFoodListQueryKey } from '@shared/api/lib/sold-goods/sold-goods';
import {
  ActivityCoverage,
  FoodActivityRecordUpdate,
  FoodActivityRetrieveUpdate,
  PaginatedFoodActivitiesListList,
  PaginatedFoodActivityRecordList,
  PurchasedGoodsFoodListParams,
  PurchasedGoodsFoodPreviewRetrieveParams,
  PurchasedGoodsFoodRecordsListParams,
  PurchasedGoodsFoodSpendRetrieveParams,
  PurchasedGoodsSpend,
} from '@shared/api/types';
import { useQueryClient } from '@tanstack/react-query';

export const useGetPurchasedFoodAndDrinkList = (
  params?: PurchasedGoodsFoodListParams,
  { enabled }: { enabled?: boolean } = {}
) => {
  return usePurchasedGoodsFoodList<PaginatedFoodActivitiesListList>(params, {
    request: useAxiosConfig(),
    query: {
      keepPreviousData: true,
      enabled,
    },
  });
};

export const useGetInfinitePurchasedFoodAndDrinkList = (
  params?: PurchasedGoodsFoodListParams
) => {
  return usePurchasedGoodsFoodListInfinite<PaginatedFoodActivitiesListList>(
    params,
    {
      request: useAxiosConfig(),
      query: {
        getNextPageParam: (lastPage, pages) => {
          return lastPage.next ? pages.length + 1 : undefined;
        },
      },
    }
  );
};

export const useGetPurchasedFoodAndDrinkItem = ({
  activityUuid,
}: {
  activityUuid: string;
}) => {
  return usePurchasedGoodsFoodRetrieve<FoodActivityRetrieveUpdate>(
    activityUuid,
    {
      request: useAxiosConfig(),
    }
  );
};

export const useGetPurchasedFoodAndDrinkDetail = ({
  activityUuid,
}: {
  activityUuid: string;
}) => {
  return usePurchasedGoodsFoodRetrieve<FoodActivityRetrieveUpdate>(
    activityUuid,
    {
      request: useAxiosConfig(),
    }
  );
};

export const usePatchPurchasedFoodAndDrinkItem = () => {
  const queryClient = useQueryClient();
  return usePurchasedGoodsFoodPartialUpdate<FoodActivityRetrieveUpdate>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () =>
        Promise.all([
          refreshQueries(queryClient, getPurchasedGoodsFoodListQueryKey()),
          refreshQueries(queryClient, getSoldGoodsFoodListQueryKey()),
          refreshQueries(queryClient, getAppIngredientPickerListQueryKey()),
        ]),
    },
  });
};

export const useGetPurchasedFoodAndDrinkPreview = ({
  params,
  enabled,
}: {
  params?: PurchasedGoodsFoodPreviewRetrieveParams;
  enabled?: boolean;
}) => {
  return usePurchasedGoodsFoodPreviewRetrieve(params, {
    request: useAxiosConfig(),
    query: {
      enabled: enabled,
      cacheTime: 0,
      staleTime: 0,
    },
  });
};

export const useDeletePurchasedFoodAndDrinkItem = () => {
  const queryClient = useQueryClient();
  return usePurchasedGoodsDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () =>
        Promise.all([
          refreshQueries(queryClient, getPurchasedGoodsFoodListQueryKey()),
          refreshQueries(queryClient, getAppIngredientPickerListQueryKey()),
        ]),
    },
  });
};

export const useGetPurchasedFoodAndDrinkItemRecords = ({
  activityUuid,
  params,
}: {
  activityUuid: string;
  params?: PurchasedGoodsFoodRecordsListParams;
}) => {
  return usePurchasedGoodsFoodRecordsList<PaginatedFoodActivityRecordList>(
    activityUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        keepPreviousData: true,
      },
    }
  );
};

export const useGetPurchasedFoodAndDrinkItemSpend = ({
  activityUuid,
  params,
}: {
  activityUuid: string;
  params?: PurchasedGoodsFoodSpendRetrieveParams;
}) => {
  return usePurchasedGoodsFoodSpendRetrieve<PurchasedGoodsSpend>(
    activityUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        keepPreviousData: true,
      },
    }
  );
};

export const useUpdatePurchasedFoodAndDrinkItemRecord = () => {
  const queryClient = useQueryClient();
  return usePurchasedGoodsFoodRecordsUpdate<FoodActivityRecordUpdate>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async (_, variables) => {
        await queryClient.refetchQueries(
          getPurchasedGoodsFoodRecordsListQueryKey(variables.activityUuid)
        );
      },
    },
  });
};

export const usePatchPurchasedFoodAndDrinkItemRecord = () => {
  const queryClient = useQueryClient();
  return usePurchasedGoodsFoodRecordsPartialUpdate<FoodActivityRecordUpdate>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async (_, variables) => {
        await queryClient.refetchQueries(
          getPurchasedGoodsFoodRecordsListQueryKey(variables.activityUuid)
        );
      },
    },
  });
};

export const useDeletePurchasedFoodAndDrinkItemRecord = () => {
  const queryClient = useQueryClient();
  return usePurchasedGoodsRecordsDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async (_, variables) => {
        await queryClient.refetchQueries(
          getPurchasedGoodsFoodRecordsListQueryKey(variables.activityUuid)
        );
      },
    },
  });
};

export const useGetPurchasedFoodAndDrinkCoverage = () => {
  return usePurchasedGoodsFoodCoverageRetrieve<ActivityCoverage>({
    request: useAxiosConfig(),
  });
};

/**
 * Bulk actions on purchased food (confirm / model-as-basic-food /
 * model-as-recipe) now go through the unified `useBulkUpdatePurchasedGoods`
 * hook in `./bulk` — the action travels inside the payload.
 */
